import axios from "axios"
import { baseApi } from "../config"

/**
 * The HTTP client.
 *
 * One instance so the token, the error shape and the sign-out behaviour are
 * defined once. Every API module below imports this rather than calling axios
 * directly — the moment two clients exist, one of them is missing the interceptor
 * that matters.
 */
const http = axios.create({
  baseURL: baseApi,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
})

const TOKEN_KEY = "tallywise.token"

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Private windows and blocked site data both throw here rather than
    // returning null, which would take the whole app down on load.
    return null
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Not being able to persist the token is survivable — the session lasts
    // until the tab closes. Failing the login is not.
  }
}

http.interceptors.request.use((config) => {
  const token = getStoredToken()
  if (token) config.headers.Authorization = `Bearer ${token}`

  /*
   * A multipart body must NOT carry this instance's default Content-Type.
   *
   * The instance sets `application/json` for every request, which is right for
   * all of them except an upload. A FormData body needs
   * `multipart/form-data; boundary=…`, and that boundary is generated by the
   * browser when it serialises the body — so any Content-Type we set here
   * overwrites it with one that has no boundary.
   *
   * The failure is quiet and confusing: the request succeeds, the server parses
   * a body with no parts, and the endpoint answers "no files were uploaded"
   * while the network tab shows the bytes going out.
   */
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers["Content-Type"]
    delete config.headers["content-type"]
  }

  return config
})

/** Called when the server says the session is gone. Set by AuthContext. */
let onUnauthorized = null
export function setUnauthorizedHandler(handler) {
  onUnauthorized = handler
}

http.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status
    const code = error.response?.data?.code

    /*
     * A 401 means the session is genuinely gone — expired, revoked, or signed out
     * on another device. Clear it and let the app show the login screen.
     *
     * A 403 is NOT that. It means "you are signed in and may not do this", and
     * signing somebody out for clicking something they lack permission for is
     * both wrong and infuriating. The distinction is easy to collapse by accident
     * and very visible when you do.
     */
    if (status === 401 && ["SESSION_INVALID", "TOKEN_EXPIRED", "TOKEN_INVALID"].includes(code)) {
      setStoredToken(null)
      if (onUnauthorized) onUnauthorized(code)
    }

    // A consistent message, so no screen has to unpick the error shape itself.
    error.userMessage =
      error.response?.data?.message ||
      (error.code === "ECONNABORTED" ? "That took too long. Try again." : "Something went wrong.")

    return Promise.reject(error)
  },
)

export default http
